import type { OpenAPIResponse, OpenAPIResponseData, UserAPIInterface, PhotoAPIInterface } from './api-switcher'
import type { paths } from './auto-gen-types'

/**
 * Generate user icon URL from user ID
 * @param userId - The user ID
 * @returns User icon URL
 */
export const generateUserIconUrl = (userId: string): string =>
  `https://content.metamory.cc/icon/user/${userId}`

/**
 * Convert OpenAPIResponse data, preserving error state
 * @param res - The OpenAPI response to convert
 * @param changer - Function to transform successful response data
 * @returns Transformed OpenAPI response
 */
export const responseDataConverter = <T, R>(
  res: OpenAPIResponse<NonNullable<T>>,
  changer: (data: NonNullable<T>) => R
): OpenAPIResponse<R> =>
  res.then(({ data, error, response }) => {
    return error ?
      { error, response }
      :
      {
        data: changer(data!),
        response
      }
  })

// Type aliases for API responses
type APIUser = paths['/user/{UserId}']['get']['responses']['200']['content']['application/json']
type User = OpenAPIResponseData<ReturnType<UserAPIInterface['getUser']>>

/**
 * Convert API user object to internal User type
 * @param user - API user object
 * @returns Converted user object
 */
export const apiUserToUser = (user: APIUser): User => ({
  ...user,
  description: user.description || '',
  vrchat_user_id: user.vrchat_user_id || '',
  twitter_id: user.twitter_id || '',
  discord_id: user.discord_id || '',
  vrchat_display_name: user.vrchat_display_name || '',
  is_mute: user.is_mute || false,
  is_frozen: false, // TODO: API側でis_frozenフィールドを追加する必要があります
  in_book: undefined, // TODO: Fix type issue with user.in_book
  user_icon: generateUserIconUrl(user.user_id)
})

type APIPhoto = paths['/my/{PhotoId}']['get']['responses']['200']['content']['application/json']
type Photo = OpenAPIResponseData<ReturnType<PhotoAPIInterface['getPhoto']>>

/**
 * Convert API photo object to internal Photo type (without dates)
 * @param photo - API photo object
 * @returns Converted photo object without created/added dates
 */
export const apiPhotoToPhoto = (photo: APIPhoto): Omit<Photo, 'created' | 'added'> => ({
  ...photo,
  exif: '', // TODO: EXIF data not yet available
  reactions: Object.fromEntries(
    Object.entries(photo.reactions || {}).map(([key, users]) =>
      [key, users.map(u => apiUserToUser({
        ...u as any,
        metamory_id: (u as any).user_metamory_id || (u as any).metamory_id // Handle both field names
      } as APIUser))]
    )
  ),
  world_id: photo.world_id || '',
  world_name: photo.world_name || '',
  favorite: false, // TODO: Favorite status not yet available
  owner: apiUserToUser(photo.owner),
  in_users: photo.in_users.map(apiUserToUser),
  in_users_candidate: photo.in_users_candidate.map(apiUserToUser),
})

/**
 * Convert API photo to MyPhoto type with dates
 * @param photo - API photo object
 * @returns Converted photo object with created/added dates
 */
export const apiPhotoToMyPhoto = (photo: APIPhoto): Photo => ({
  ...apiPhotoToPhoto(photo),
  created: new Date(photo.taken_at),
  added: new Date(photo.taken_at),
})

type APITrashItems = paths['/trash/items']['get']['responses']['200']['content']['application/json']
type TrashItemsPage = OpenAPIResponseData<ReturnType<PhotoAPIInterface['listTrashItems']>>

/**
 * Convert a GET /trash/items page. Photos keep MyPhoto dates (added = taken_at);
 * the time the photo was trashed is the separate trashed_at.
 * @param page - API trash items page
 * @returns Converted page with Date trashed_at
 */
export const apiTrashItemsToTrashPage = (page: APITrashItems): TrashItemsPage => ({
  items: page.items.map(({photo, trashed_at}) => ({
    photo: apiPhotoToMyPhoto(photo),
    trashed_at: new Date(trashed_at),
  })),
  next_cursor: page.next_cursor,
  total_count: page.total_count,
})

/**
 * Convert API photo to DM photo type with DM context-aware timestamps
 * @param photo - API photo object from DM endpoint
 * @returns Converted photo object with DM-specific added date (sent timestamp)
 */
export const apiPhotoToDMPhoto = (photo: APIPhoto): Photo => ({
  ...apiPhotoToPhoto(photo),
  created: new Date(photo.taken_at),
  added: new Date(photo.added_at), // This is the DM sent timestamp from DMPhotoContext
})

/**
 * Convert API photo to share photo type with share context-aware timestamps
 * @param photo - API photo object from share endpoint
 * @returns Converted photo object with share-specific added date (when added to share)
 */
export const apiPhotoToSharePhoto = (photo: APIPhoto): Photo => ({
  ...apiPhotoToPhoto(photo),
  created: new Date(photo.taken_at),
  added: new Date(photo.added_at), // This is the share-specific added timestamp from SharePhotoContext
})

type APIMe = paths['/me']['get']['responses']['200']['content']['application/json']
type Me = OpenAPIResponseData<ReturnType<UserAPIInterface['getMe']>>

/**
 * Convert API Me object to internal Me type
 * @param me - API me object
 * @returns Converted me object
 */
export const apiMeToMe = (me: APIMe): Me => ({
  ...me,
  ...apiUserToUser(me),
  clients: me.clients?.map(client => ({
    ...client,
    last_activity_at: new Date(client.last_activity_at),
  })) || [],
  infomations: [],
  clear_tutorials: me.clear_tutorials as ('profile' | 'share' | 'pc_client')[],
  // Older servers may omit the field during rollout.
  seen_hints: me.seen_hints ?? [],
  plan_id: me.plan_id as any, // Cast to PlanId type
  next_plan_id: me.next_plan_id as any,
  next_plan_renewal_at: me.next_plan_renewal_at ? new Date(me.next_plan_renewal_at) : undefined,
  birth_date: me.birth_date ? new Date(me.birth_date) : undefined,
  birth_date_changed_at: me.birth_date_changed_at ? new Date(me.birth_date_changed_at) : undefined,
  global_sensitive_filter_enabled: me.global_sensitive_filter_enabled,
  sticker_used_count: me.sticker_used_count,
  sticker_quota: me.sticker_quota,
})
